import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Building2,
  ShoppingBag,
  CreditCard,
  Check,
} from "lucide-react";
import { InteractiveMap, SeoHead } from "@shared/components";
import {
  generateLocalBusinessSchema,
  generateBreadcrumbSchema,
} from "@shared/utils/schemaOrg";

export function Contact() {
  // Coordenadas de Mendoza Capital (Argentina) - Actualizadas
  const businessLocation: [number, number] = [
    -32.87284410944235, -68.8195651711744,
  ];

  // URL de Google Maps con las coordenadas
  const googleMapsUrl = `https://www.google.com/maps?q=${businessLocation[0]},${businessLocation[1]}`;

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Contacto", url: "/contact" },
  ];
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);
  const localBusinessSchema = generateLocalBusinessSchema();

  return (
    <>
      <SeoHead
        title="Contacto"
        description="Contacta con Cerámicas Gassull en Mendoza, Argentina. Más de 25 años de experiencia en cerámicas y materiales de construcción. Teléfono: +54 9 2617 13-5406 | Email: gassullceramica@gmail.com"
        keywords="contacto, cerámicas Gassull, Mendoza, Argentina, materiales construcción, teléfono, email, dirección"
        canonical="/contact"
        schemaJsonLd={[breadcrumbSchema, localBusinessSchema]}
      />
      <div className="min-h-screen">
        {/* Header de la página - Consistente con Products y otras páginas */}
        <section className="bg-gray-50 py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Contacto
              </h1>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-2">
                Estamos aquí para ayudarte con tu proyecto
              </p>
            </div>
          </div>
        </section>

        {/* Historia de la Empresa */}
        <section className="py-6 sm:py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center mb-4 sm:mb-6">
                <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-[#282828] mr-2 sm:mr-3" />
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Nuestra Historia
                </h2>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
                <div className="space-y-3 sm:space-y-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                  <p>
                    La historia de nuestra empresa comienza con{" "}
                    <strong className="text-[#282828]">
                      Don José Gassull Garriga
                    </strong>
                    , natural de <strong>Verdú</strong>, provincia de{" "}
                    <strong>Lérida</strong>, región de <strong>Cataluña</strong>
                    , lugar al que dio origen el logo de la Empresa.
                  </p>

                  <p>
                    Desembarcó en el puerto de <strong>Buenos Aires</strong> a
                    la edad de <strong>12 años</strong> para luego radicarse en
                    la provincia de <strong>Mendoza</strong>.
                  </p>

                  <p>
                    Durante la <strong>década del 40</strong>, toma la
                    representación de <strong>Cerámica Alberdi</strong>, en la
                    región de <strong>Cuyo</strong>. Empresa pionera en el país
                    en la fabricación de pisos extrudidos (coloniales), gres
                    cerámicos y ladrillos huecos.
                  </p>

                  <p>
                    Luego del <strong>terremoto de San Juan</strong>, en el año{" "}
                    <strong>1944</strong>, Cerámica Alberdi le encomendó a Don
                    José poner en pie una nueva planta de ladrillos y tejas en
                    la zona de Cuyo, por lo cual, y en el lapso de{" "}
                    <strong>6 meses</strong> se inauguró en{" "}
                    <strong>San José de Guaymallén</strong> dicha planta.
                  </p>

                  <p>
                    La empresa desempeñó su actividad en su tradicional casona
                    de <strong>calle 9 de Julio</strong>, frente a la
                    <strong> plaza España</strong>, en la ciudad de Mendoza.
                  </p>

                  <p>
                    Sus dos hijos, <strong>Carlos y Juan</strong>, acompañaron
                    su trabajo e incorporando otros rubros a los existentes.
                  </p>

                  <p>
                    Actualmente la Empresa, con su{" "}
                    <strong>tercera generación</strong>, vende, distribuye y
                    asesora una variada gama de productos para la construcción,
                    como son viguetas pretensadas, aislantes térmicos,
                    hidráulicos y acústicos, como también revestimientos,
                    porcelanatos, refractarios, etc.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Información de Contacto y Mapa */}
        <section className="py-6 sm:py-10 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              {/* Información de Contacto */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Información de Contacto
                </h2>

                <div className="space-y-4 sm:space-y-6">
                  {/* Dirección */}
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="flex-shrink-0">
                      <div className="bg-[#282828] rounded-lg p-2 sm:p-3">
                        <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                        Dirección
                      </h3>
                      <a
                        href={googleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm sm:text-base text-gray-600 leading-relaxed hover:text-[#282828] transition-colors block"
                      >
                        Lateral Oeste de Costanera Norte y, Bajada de Arrollabes
                        <span className="text-gray-500">
                          {" "}
                          (A una cuadra de la rotonda del avión)
                        </span>
                        <br />
                        M5539 Las Heras, Mendoza
                        <br />
                        Argentina
                      </a>
                    </div>
                  </div>

                  {/* Teléfono */}
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="flex-shrink-0">
                      <div className="bg-[#282828] rounded-lg p-2 sm:p-3">
                        <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                        Teléfono
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 break-words">
                        <a
                          href="tel:+5492617135406"
                          className="hover:text-[#282828] transition-colors"
                        >
                          +54 9 2617 13-5406
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="flex-shrink-0">
                      <div className="bg-[#282828] rounded-lg p-2 sm:p-3">
                        <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                        Email
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 break-words">
                        <a
                          href="mailto:gassullceramica@gmail.com"
                          className="hover:text-[#282828] transition-colors break-all"
                        >
                          gassullceramica@gmail.com
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Horarios */}
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="flex-shrink-0">
                      <div className="bg-[#282828] rounded-lg p-2 sm:p-3">
                        <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                        Horarios de Atención
                      </h3>
                      <div className="text-sm sm:text-base text-gray-600 space-y-1">
                        <p>Lunes a Viernes: 8:00 - 18:00</p>
                        <p>Sábados: 8:00 - 13:00</p>
                        <p>Domingos: Cerrado</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botones de acción */}
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 space-y-2 sm:space-y-3">
                  <a
                    href="tel:+5492617135406"
                    className="w-full inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-[#282828] text-white rounded-lg text-sm sm:text-base font-medium hover:bg-[#1a1a1a] transition-colors shadow-sm"
                  >
                    <Phone className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    Llamar Ahora
                  </a>
                  <a
                    href="mailto:gassullceramica@gmail.com"
                    className="w-full inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-white text-[#282828] border-2 border-[#282828] rounded-lg text-sm sm:text-base font-medium hover:bg-[#282828] hover:text-white transition-colors shadow-sm"
                  >
                    <Mail className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    Enviar Email
                  </a>
                </div>
              </div>

              {/* Mapa */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  Nos Encontramos en
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Lateral Oeste de Costanera Norte y, Bajada de Arrollabes (A
                  una cuadra de la rotonda del avión), M5539 Las Heras, Mendoza
                </p>
                <div className="w-full h-[300px] sm:h-[450px]">
                  <InteractiveMap
                    position={businessLocation}
                    placeName="Cerámicas Gassull"
                    address="Lateral Oeste de Costanera Norte y, Bajada de Arrollabes (A una cuadra de la rotonda del avión), M5539 Las Heras, Mendoza, Argentina"
                    zoom={15}
                    height="100%"
                    className="rounded-lg border border-gray-200 w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Servicios, Planificación y Métodos de Pago */}
        <section className="py-6 sm:py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Nuestros Servicios
              </h2>
              <p className="text-base sm:text-lg text-gray-600 px-2">
                Opciones flexibles para tu comodidad
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Opciones de Servicio */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                  <div className="bg-[#282828] rounded-lg p-2 mr-2 sm:mr-3">
                    <ShoppingBag className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <span className="text-base sm:text-lg">
                    Opciones de servicio
                  </span>
                </h3>
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-[#282828] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      Entrega a domicilio
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-[#282828] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      Compras en tienda
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-[#282828] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      Retiro en tienda
                    </span>
                  </li>
                </ul>
              </div>

              {/* Métodos de Pago */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                  <div className="bg-[#282828] rounded-lg p-2 mr-2 sm:mr-3">
                    <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <span className="text-base sm:text-lg">Pagos</span>
                </h3>
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-[#282828] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      Pagos móviles mediante NFC
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-[#282828] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      Tarjetas de débito
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-[#282828] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      Tarjetas de crédito
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
